import { Button, makeStyles } from '@material-ui/core';
import { AdditionalBillingDocument } from 'doc-mate-store/lib/models/AdditionalBillingDocument';
import { LoadDocument as LDocument } from 'doc-mate-store/lib/models/LoadDocument';
import React, { useState } from 'react';
import { PdfThumbnail } from '../PDFThumbnail';

// Support both document types
type AnyDocument = LDocument | AdditionalBillingDocument;

type FileItem = {
  id: string | number;
  file: string;
  name: string;
};

type Props = {
  files: FileItem[];
  document: AnyDocument | undefined;
  isEditable: boolean;
  onDeleteConfirm: (document: AnyDocument, id: string | number) => void;
};

const ReorderableFileList: React.FC<Props> = ({
  files,
  document,
  isEditable,
  onDeleteConfirm,
}) => {
  const classes = useStyles();
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    if (!isEditable) return;
    e.stopPropagation();
    e.dataTransfer.setData('application/x-reorder-file', `${index}`);
    e.dataTransfer.setData('text/plain', `${index}`);
    e.dataTransfer.effectAllowed = 'move';
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    if (!isEditable) return;
    const types = Array.from(e.dataTransfer.types || []);

    if (!types.includes('application/x-reorder-file')) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, targetIndex: number) => {
    if (!isEditable) return;
    const types = Array.from(e.dataTransfer.types || []);
    if (!types.includes('application/x-reorder-file')) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    setDragOverIndex(null);
    setDraggedIndex(null);

    const sourceIndexStr = e.dataTransfer.getData('application/x-reorder-file');
    const sourceIndex = sourceIndexStr
      ? parseInt(sourceIndexStr, 10)
      : draggedIndex;

    if (
      sourceIndex === null ||
      isNaN(sourceIndex) ||
      sourceIndex === targetIndex
    ) {
      return;
    }

    const updatedFiles = [...files];
    const [movedFile] = updatedFiles.splice(sourceIndex, 1);
    updatedFiles.splice(targetIndex, 0, movedFile);

    const reorderedIds = updatedFiles.map(f => f.id);
    if (document && typeof (document as any).reorderFiles === 'function') {
      (document as any).reorderFiles(reorderedIds);
    }
  };

  return (
    <div className={classes.fileListWrapper}>
      {files.map((file, index) => {
        const isBeingDragged = draggedIndex === index;
        const isTargetOver = dragOverIndex === index;

        return (
          <div
            className={`${classes.fileWrapper} ${
              isTargetOver ? classes.fileWrapperDragOver : ''
            } ${isBeingDragged ? classes.fileWrapperDragging : ''}`}
            key={file.id}
            draggable={isEditable}
            onDragStart={e => handleDragStart(e, index)}
            onDragOver={e => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={e => handleDrop(e, index)}
          >
            {isEditable && (
              <span className={classes.dragHandle} title="Drag to reorder">
                ⋮⋮
              </span>
            )}
            <a
              href={file.file}
              className={classes.file}
              download
              target="_blank"
              rel="noreferrer"
            >
              <PdfThumbnail pdfUrl={file.file} width={80} onClick={() => {}} />
              <span className={classes.fileName}>{file.name}</span>
            </a>
            {isEditable && document && (
              <Button
                className={classes.button}
                onClick={() => onDeleteConfirm(document, file.id)}
              >
                Delete
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  fileListWrapper: {
    marginTop: 4,
  },
  fileWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '2px 4px',
    borderRadius: 4,
    transition: 'all 0.15s ease-in-out',
  },
  fileWrapperDragOver: {
    backgroundColor: 'rgba(13, 55, 75, 0.08)',
    outline: '1px dashed #0d374b',
  },
  fileWrapperDragging: {
    opacity: 0.4,
  },
  dragHandle: {
    cursor: 'grab',
    color: '#0d374b',
    fontSize: 16,
    paddingRight: 4,
    userSelect: 'none',
  },
  file: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    marginLeft: 8,
    marginTop: 4,
    color: '#0d374b',
    textDecoration: 'none',
  },
  fileName: {
    fontSize: 11,
    textOverflow: 'hidden',
    marginLeft: 2,
    marginRight: 2,
  },
  button: {
    padding: 0,
    fontWeight: 700,
    marginLeft: theme.spacing(0.5),
    background: theme.palette.error.main,
    color: 'white',
    '&:hover': {
      color: theme.palette.error.main,
    },
  },
}));

export default ReorderableFileList;
